/**
 * The classifications a prompt can be filed under, in one place.
 *
 * A prompt's `target_field` is its classification: it says what the instruction is
 * written to work on, which is what decides where the prompt is offered first and —
 * with `sort_order` — where it sits in that list. The same six values were spelled
 * out separately on the prompts page and in the studio, which is how the studio came
 * to be missing one of them; they live here now and both read this.
 *
 * `group` is presentation only. It exists because the difference an editor cares
 * about is what the model is handed — a passage of text, or a picture of a page —
 * and that difference is invisible in a flat list of six field keys.
 */

export const PROMPT_GROUPS = [
  {
    key: 'text',
    label: 'Text',
    note: 'The model is handed the words in a field and returns words to put back.',
  },
  {
    key: 'extract',
    label: 'Extraction from text',
    note: 'Reads one field and writes another, so the result lands somewhere else.',
  },
  {
    key: 'vision',
    label: 'Images and PDFs',
    note: 'The model is handed a picture — a photographed page, a scan, a photograph.',
  },
]

export const PROMPT_FIELDS = [
  {
    value: 'newsletters.body_html',
    label: 'Update text',
    group: 'text',
    hint: 'Proofreading, rewriting and translating the body of an update.',
  },
  {
    value: 'newsletters.prayer_requests',
    label: 'Prayer Requests',
    group: 'text',
    hint: 'Tidying and reshaping a request list that is already there.',
  },
  {
    value: 'partners.description',
    label: 'Partner unit description',
    group: 'text',
    hint: 'The description on a partner unit record.',
  },
  {
    value: 'newsletters.extract_prayer',
    label: 'Extract prayer requests from the update',
    group: 'extract',
    hint: 'Reads the update text and writes the Prayer Requests field.',
  },
  {
    value: 'newsletters.import_file',
    label: 'Text from a photo or PDF scan',
    group: 'vision',
    hint: 'Transcription: a vision model reads the page and returns its text.',
  },
  {
    value: 'media.alt_text',
    label: 'Image alt text',
    group: 'vision',
    hint: 'Captions and descriptions for an uploaded picture.',
  },
]

export const promptField = (value) => PROMPT_FIELDS.find((f) => f.value === value) ?? null

/** Falls back to the raw key: a prompt filed under a field this list forgot is still a prompt. */
export const promptFieldLabel = (value) => promptField(value)?.label ?? value

/** The fields grouped for display, in PROMPT_GROUPS order, empty groups dropped. */
export const promptFieldGroups = (fields = PROMPT_FIELDS) =>
  PROMPT_GROUPS.map((g) => ({ ...g, fields: fields.filter((f) => f.group === g.key) })).filter(
    (g) => g.fields.length
  )
